import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import './i18n/i18n'; // Initialize i18n
import i18n from './i18n/i18n';

// Pages
import Landing from './pages/Landing';
import Introduction from './pages/Introduction';
import Auth from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Onboarding from './pages/Onboarding';
import VisionRevelation from './pages/VisionRevelation';
import ContentRevelation from './pages/ContentRevelation';
import FirstAction from './pages/FirstAction';
import ProfileRevelation from './pages/ProfileRevelation';
import BrandAudit from './pages/BrandAudit';
import VisionIdentity from './pages/VisionIdentity';
import VisionBoard from './pages/VisionBoard';
import InstagramStrategy from './pages/InstagramStrategy';
import ContentGenerator from './pages/ContentGenerator';
import PhotoshootConcept from './pages/PhotoshootConcept';
import VisualRoadmap from './pages/VisualRoadmap';
import FutureBrand from './pages/FutureBrand';
import VisualCard from './pages/VisualCard';
import Challenges from './pages/Challenges';
import Community from './pages/Community';
import Coaching from './pages/Coaching';
import Pricing from './pages/Pricing';
import Join from './pages/Join';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import PaymentSuccess from './pages/PaymentSuccess';
import Settings from './pages/Settings';
import BrandGlowUp from './pages/BrandGlowUp';
import Reviews from './pages/Reviews';
import Experiences from './pages/Experiences';
import VisualStudio from './pages/VisualStudio';
import BrandIdentityCard from './pages/BrandIdentityCard';
import Referral from './pages/Referral';
import ContentPlanningStudio from './pages/ContentPlanningStudio';
import ContentStrategy from './pages/ContentStrategy';
import WomenConnect from './pages/WomenConnect';
import BusinessDirectory from './pages/BusinessDirectory';
import EarlyAccess from './pages/EarlyAccess';
import BrandJourney from './pages/BrandJourney';
import AIAssistant from './pages/AIAssistant';
import MonthlyJournal from './pages/MonthlyJournal';
import QuarterlyReview from './pages/QuarterlyReview';
import AnnualReport from './pages/AnnualReport';
import VisionSetup from './pages/VisionSetup';
import VisionBoardGenerator from './pages/VisionBoardGenerator';
import AdminScheduledPosts from './pages/AdminScheduledPosts';
import AdminFeedback from './pages/AdminFeedback';
import FounderAdmin from './pages/FounderAdmin';
import InviteFriend from './pages/InviteFriend';
import JoinBeta from './pages/JoinBeta';
import AdminEmails from './pages/AdminEmails';
import AdminLeaderboardFix from './pages/AdminLeaderboardFix';
import UpgradeExperience from './pages/UpgradeExperience';
import CGV from './pages/CGV';
import CGU from './pages/CGU';
import PrivacyPolicy from './pages/PrivacyPolicy';
import LegalNotice from './pages/LegalNotice';
import ContinueJourney from './pages/ContinueJourney';
import BetaWaiting from './pages/BetaWaiting';
import JourneyStart from './pages/JourneyStart';
import JourneyEvolution from './pages/JourneyEvolution';
import LegalConsent from './pages/LegalConsent';
import MyRevelations from './pages/MyRevelations';
import VisionPlan from './pages/VisionPlan';
import MySubscription from './pages/MySubscription';
import FoundingVisionaryWelcome from './components/FoundingVisionaryWelcome';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// GLOBAL FLAG: Controls pricing visibility
// true = Show pricing, require payment for non-beta users
// false = Hide pricing, allow free access for everyone
export const SHOW_PRICING = true;

// BETA PHASE ENDED: New users must pay, beta users keep free access
// Beta users are identified by is_beta_user: true in their profile

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Get token directly from localStorage to ensure we have the latest
    const currentToken = localStorage.getItem('token');
    
    if (!currentToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      
      setUser(response.data);
      setToken(currentToken);
      
      // Set language from user preference
      if (response.data?.language) {
        i18n.changeLanguage(response.data.language);
        // Handle RTL for Arabic
        if (response.data.language === 'ar') {
          document.documentElement.dir = 'rtl';
          document.documentElement.lang = 'ar';
        } else {
          document.documentElement.dir = 'ltr';
          document.documentElement.lang = response.data.language;
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setToken(null);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {  });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, login, register, logout, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route with Beta check and Legal Consent check
const ProtectedRoute = ({ children }) => {
  const { user, loading, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [betaChecked, setBetaChecked] = useState(false);
  const [canAccessApp, setCanAccessApp] = useState(null);
  const [accessStatus, setAccessStatus] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(false);

  // Check Beta status and Legal Consent
  useEffect(() => {
    const checkAccess = async () => {
      if (!user || !token) {
        setBetaChecked(true);
        setConsentChecked(true);
        return;
      }

      // Check if consent was just accepted (bypass API check to avoid race condition)
      const consentJustAccepted = localStorage.getItem('consent_just_accepted');
      if (consentJustAccepted) {
        localStorage.removeItem('consent_just_accepted');
        setNeedsConsent(false);
        setBetaChecked(true);
        setConsentChecked(true);
        setCanAccessApp(true);
        return;
      }

      try {
        // Check beta status
        const betaResponse = await axios.get(`${API}/beta/user-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCanAccessApp(betaResponse.data.can_access_app);
        setAccessStatus(betaResponse.data.access_status);
        
        // Store founding visionary info
        if (betaResponse.data.is_founding_visionary) {
          localStorage.setItem('is_founding_visionary', 'true');
          localStorage.setItem('needs_founding_welcome', betaResponse.data.needs_founding_welcome ? 'true' : 'false');
        }
        
        // Check legal consent status
        const consentResponse = await axios.get(`${API}/legal/consent-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNeedsConsent(consentResponse.data.needs_consent);
      } catch (error) {
        // If checks fail, allow access (fail-open for better UX)
        console.error('Access check failed:', error);
        setCanAccessApp(true);
        setNeedsConsent(false);
      } finally {
        setBetaChecked(true);
        setConsentChecked(true);
      }
    };

    checkAccess();
  }, [user, token]);

  // State for founding welcome modal
  const [showFoundingWelcome, setShowFoundingWelcome] = useState(false);
  
  useEffect(() => {
    // Check if we should show founding welcome
    const isFoundingVisionary = localStorage.getItem('is_founding_visionary') === 'true';
    const needsWelcome = localStorage.getItem('needs_founding_welcome') === 'true';
    if (isFoundingVisionary && needsWelcome && canAccessApp) {
      setShowFoundingWelcome(true);
    }
  }, [canAccessApp]);

  const handleFoundingWelcomeContinue = () => {
    localStorage.setItem('needs_founding_welcome', 'false');
    setShowFoundingWelcome(false);
  };

  if (loading || !betaChecked || !consentChecked) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#E5C585] animate-pulse font-serif text-2xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Check legal consent first (unless already on legal-consent page)
  if (needsConsent && location.pathname !== '/legal-consent') {
    return <Navigate to="/legal-consent" replace />;
  }

  // If user cannot access app (no active subscription)
  if (canAccessApp === false) {
    // Founding Visionaries (beta users) need to subscribe too now
    // But they get to see the founding welcome and discounted pricing
    const isFoundingVisionary = user?.is_beta_user || user?.beta_access || user?.is_founding_visionary;
    
    // Redirect all users without subscription to pricing
    if (SHOW_PRICING) {
      return <Navigate to="/pricing" replace />;
    }
    
    // Fallback: redirect to beta-waiting
    return <Navigate to="/beta-waiting" replace />;
  }

  // Show Founding Visionary welcome if needed
  if (showFoundingWelcome) {
    return (
      <>
        <FoundingVisionaryWelcome onContinue={handleFoundingWelcomeContinue} />
        {children}
      </>
    );
  }

  return children;
};

// Admin Route - bypasses onboarding/journey checks for admin pages
const AdminRoute = ({ children }) => {
  const { user, loading, token } = useAuth();
  const location = useLocation();

  // Simple loading state - no beta check needed for admin routes
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#E5C585] animate-pulse font-serif text-2xl">Loading...</div>
      </div>
    );
  }

  if (!user || !token) {
    // Save the intended destination for after login
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Admin routes bypass ALL checks - just render the children
  // The admin page itself will check if user is admin
  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/rejoindre" element={<Join />} />
      <Route path="/introduction" element={<Introduction />} />
      <Route path="/early-access" element={<EarlyAccess />} />
      <Route path="/early-access/:referralCode" element={<EarlyAccess />} />
      <Route path="/auth" element={<Auth />} />
      {/* Pricing route - only available when SHOW_PRICING is true */}
      <Route path="/pricing" element={SHOW_PRICING ? <Pricing /> : <Navigate to="/dashboard" replace />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/onboarding" element={
        <ProtectedRoute><Onboarding /></ProtectedRoute>
      } />
      <Route path="/legal-consent" element={
        <ProtectedRoute><LegalConsent /></ProtectedRoute>
      } />
      <Route path="/upgrade-experience" element={
        <ProtectedRoute><UpgradeExperience /></ProtectedRoute>
      } />
      <Route path="/profile-revelation" element={
        <ProtectedRoute><ProfileRevelation /></ProtectedRoute>
      } />
      <Route path="/vision-revelation" element={
        <ProtectedRoute><VisionRevelation /></ProtectedRoute>
      } />
      <Route path="/content-revelation" element={
        <ProtectedRoute><ContentRevelation /></ProtectedRoute>
      } />
      <Route path="/first-action" element={
        <ProtectedRoute><FirstAction /></ProtectedRoute>
      } />
      <Route path="/vision-setup" element={
        <ProtectedRoute><VisionSetup /></ProtectedRoute>
      } />
      <Route path="/vision-board-generator" element={
        <ProtectedRoute><VisionBoardGenerator /></ProtectedRoute>
      } />
      <Route path="/brand-journey" element={
        <ProtectedRoute><BrandJourney /></ProtectedRoute>
      } />
      <Route path="/journey-start" element={
        <ProtectedRoute><JourneyStart /></ProtectedRoute>
      } />
      <Route path="/journey-evolution" element={
        <ProtectedRoute><JourneyEvolution /></ProtectedRoute>
      } />
      <Route path="/brand-audit" element={
        <ProtectedRoute><BrandAudit /></ProtectedRoute>
      } />
      <Route path="/vision-identity" element={
        <ProtectedRoute><VisionIdentity /></ProtectedRoute>
      } />
      <Route path="/vision-board" element={
        <ProtectedRoute><VisionBoard /></ProtectedRoute>
      } />
      <Route path="/instagram-strategy" element={
        <ProtectedRoute><InstagramStrategy /></ProtectedRoute>
      } />
      <Route path="/content-generator" element={
        <ProtectedRoute><ContentGenerator /></ProtectedRoute>
      } />
      <Route path="/photoshoot-concept" element={
        <ProtectedRoute><PhotoshootConcept /></ProtectedRoute>
      } />
      <Route path="/visual-roadmap" element={
        <ProtectedRoute><VisualRoadmap /></ProtectedRoute>
      } />
      <Route path="/future-brand" element={
        <ProtectedRoute><FutureBrand /></ProtectedRoute>
      } />
      <Route path="/visual-card" element={
        <ProtectedRoute><VisualCard /></ProtectedRoute>
      } />
      <Route path="/challenges" element={
        <ProtectedRoute><Challenges /></ProtectedRoute>
      } />
      <Route path="/community" element={
        <ProtectedRoute><Community /></ProtectedRoute>
      } />
      <Route path="/coaching" element={
        <ProtectedRoute><Coaching /></ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute><Settings /></ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute><PaymentSuccess /></ProtectedRoute>
      } />
      <Route path="/payment-success" element={
        <ProtectedRoute><PaymentSuccess /></ProtectedRoute>
      } />
      <Route path="/my-subscription" element={
        <ProtectedRoute><MySubscription /></ProtectedRoute>
      } />
      <Route path="/brand-glow-up" element={
        <ProtectedRoute><BrandGlowUp /></ProtectedRoute>
      } />
      <Route path="/reviews" element={
        <ProtectedRoute><Reviews /></ProtectedRoute>
      } />
      <Route path="/visual-studio" element={
        <ProtectedRoute><VisualStudio /></ProtectedRoute>
      } />
      <Route path="/brand-identity-card" element={
        <ProtectedRoute><BrandIdentityCard /></ProtectedRoute>
      } />
      <Route path="/referral" element={
        <ProtectedRoute><Referral /></ProtectedRoute>
      } />
      <Route path="/content-planning-studio" element={
        <ProtectedRoute><ContentPlanningStudio /></ProtectedRoute>
      } />
      <Route path="/content-strategy" element={
        <ProtectedRoute><ContentStrategy /></ProtectedRoute>
      } />
      <Route path="/women-connect" element={
        <ProtectedRoute><WomenConnect /></ProtectedRoute>
      } />
      <Route path="/business-directory" element={
        <ProtectedRoute><BusinessDirectory /></ProtectedRoute>
      } />
      <Route path="/ai-assistant" element={
        <ProtectedRoute><AIAssistant /></ProtectedRoute>
      } />
      <Route path="/dashboard/ai-assistant" element={
        <ProtectedRoute><AIAssistant /></ProtectedRoute>
      } />
      <Route path="/monthly-journal" element={
        <ProtectedRoute><MonthlyJournal /></ProtectedRoute>
      } />
      <Route path="/quarterly-review" element={
        <ProtectedRoute><QuarterlyReview /></ProtectedRoute>
      } />
      <Route path="/annual-report" element={
        <ProtectedRoute><AnnualReport /></ProtectedRoute>
      } />
      <Route path="/invite" element={
        <ProtectedRoute><InviteFriend /></ProtectedRoute>
      } />
      <Route path="/dashboard/invite" element={
        <ProtectedRoute><InviteFriend /></ProtectedRoute>
      } />
      <Route path="/join" element={<JoinBeta />} />
      <Route path="/admin/emails" element={
        <AdminRoute><AdminEmails /></AdminRoute>
      } />
      <Route path="/admin/scheduled-posts" element={
        <AdminRoute><AdminScheduledPosts /></AdminRoute>
      } />
      <Route path="/admin/feedback" element={
        <AdminRoute><AdminFeedback /></AdminRoute>
      } />
      <Route path="/admin/leaderboard-fix" element={
        <AdminRoute><AdminLeaderboardFix /></AdminRoute>
      } />
      <Route path="/founder-admin" element={
        <AdminRoute><FounderAdmin /></AdminRoute>
      } />
      <Route path="/experiences" element={
        <ProtectedRoute><Experiences /></ProtectedRoute>
      } />
      <Route path="/my-revelations" element={
        <ProtectedRoute><MyRevelations /></ProtectedRoute>
      } />
      <Route path="/vision-plan" element={
        <ProtectedRoute><VisionPlan /></ProtectedRoute>
      } />
      
      {/* Legal Pages - Public */}
      <Route path="/cgv" element={<CGV />} />
      <Route path="/cgu" element={<CGU />} />
      <Route path="/confidentialite" element={<PrivacyPolicy />} />
      <Route path="/mentions-legales" element={<LegalNotice />} />
      
      {/* Beta Waiting Room - After registration, before launch */}
      <Route path="/beta-waiting" element={<BetaWaiting />} />
      
      {/* Day 30 Decision Page */}
      <Route path="/continue-journey" element={<ProtectedRoute><ContinueJourney /></ProtectedRoute>} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#0A0A0A',
              border: '1px solid #D4AF37',
              color: '#ffffff',
            },
          }}
        />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
